import dayjs from 'dayjs';
import { formatCurrency } from '../../../utils/numbers';

/**
 * Row taxonomy returned by `GET /api/admin/users/:aba/:client_id/funnel`.
 *
 * The payload is one flat, ordered list: the nine fixed onboarding stages
 * (`milestone`), plus two conditional rows spliced in at the payroll-link
 * stage — the last connection failure (`error`) and, when the member got past
 * it, the recovery (`resolved`).
 */
export const FUNNEL_ROW_TYPES = Object.freeze({
    MILESTONE: 'milestone',
    ERROR: 'error',
    RESOLVED: 'resolved',
});

/**
 * Per-row `state` from the backend.
 *
 * `UNKNOWN` is the one that needs explaining: it means a LATER stage has a
 * timestamp, so the member demonstrably got past this one, but no event for it
 * survived in the lake. That is a data gap, not a stuck member, and it must not
 * render like `NOT_REACHED` — otherwise an agent reads "never signed in" for
 * somebody who has already taken an advance.
 */
export const FUNNEL_ROW_STATES = Object.freeze({
    COMPLETED: 'completed',
    FAILED: 'failed',
    UNKNOWN: 'unknown',
    NOT_REACHED: 'not_reached',
});

/**
 * Marker tones — the SCSS module keys off these (`styles.row_<tone>`), so
 * they are deliberately about how the row READS, not about the raw state:
 * a failure and a stage nobody reached are both "not completed" to the
 * backend and could not look more different to a support agent.
 */
export const FUNNEL_ROW_TONES = Object.freeze({
    DONE: 'done',
    RESOLVED: 'resolved',
    ERROR: 'error',
    GAP: 'gap',
    PENDING: 'pending',
});

/**
 * Labels for the keys that show up inside `detail`. Anything not listed is
 * humanized from its key, so a new field the backend starts sending renders
 * readably instead of disappearing.
 *
 * `code` is the provider's error code. The key is the bare `code` the funnel
 * and link-attempts payloads actually carry — the raw Athena property and the
 * ticket column are both named `error_code`, but the derivation renames it on
 * the way out, and only the name on the wire belongs here. Labelled rather
 * than humanized because "Code" alone does not say a code of what, and the
 * attempts modal renders the same field one layer above the timeline row.
 */
const DETAIL_LABELS = Object.freeze({
    platform_name: 'Platform',
    provider: 'Provider',
    advance_amount: 'Amount',
    fee: 'Fee',
    transfer_type: 'Delivery',
    error_type: 'Error type',
    code: 'Error code',
});

/**
 * Keys the timeline deliberately drops.
 *
 * `via` is the internal event name that evidenced the stage
 * (`Welcome_Viewed`, `Payroll_Link_Complete`, `Onboarding_Complete`). It
 * explains the row to us, not to the agent reading it — and the row's own
 * description already says what happened in words.
 *
 * `trigger` is the same kind of thing one layer down: the raw, lowercased
 * `trigger` property of the sign-in event that dated `account_created`. It
 * names an SDK call site, and the row's description already distinguishes the
 * three ways that stage can resolve.
 */
const HIDDEN_DETAIL_KEYS = new Set(['via', 'trigger']);

const DETAIL_CURRENCY_KEYS = new Set(['advance_amount', 'fee', 'amount']);

const MINUTE_MS = 60 * 1000;

/**
 * @param {object | null | undefined} row
 * @returns {string} one of `FUNNEL_ROW_TONES`
 */
export function getFunnelRowTone(row) {
    if (row?.type === FUNNEL_ROW_TYPES.ERROR || row?.state === FUNNEL_ROW_STATES.FAILED) {
        return FUNNEL_ROW_TONES.ERROR;
    }
    if (row?.type === FUNNEL_ROW_TYPES.RESOLVED) return FUNNEL_ROW_TONES.RESOLVED;
    if (row?.state === FUNNEL_ROW_STATES.COMPLETED) return FUNNEL_ROW_TONES.DONE;
    if (row?.state === FUNNEL_ROW_STATES.UNKNOWN) return FUNNEL_ROW_TONES.GAP;
    return FUNNEL_ROW_TONES.PENDING;
}

/**
 * Status pill for one row. Every row gets one, so a reader can name a stage's
 * state without having to infer it from the marker colour or from whether a
 * timestamp happens to be present.
 *
 * `isCurrent` wins over `UNKNOWN` on purpose: `current_stage` is the first
 * stage the member has not completed, and "where are they right now" is the
 * question this card exists to answer. The data gap is still visible in the
 * marker tone and spelled out in the row's own description.
 *
 * @param {object} row
 * @param {{ isCurrent?: boolean }} [options]
 * @returns {{ tone: string, label: string }} Never null — every row gets one.
 */
export function getFunnelRowStatus(row, { isCurrent = false } = {}) {
    const tone = getFunnelRowTone(row);

    if (tone === FUNNEL_ROW_TONES.ERROR) return { tone: 'danger', label: 'Error' };
    if (tone === FUNNEL_ROW_TONES.RESOLVED) return { tone: 'success', label: 'Resolved' };
    if (tone === FUNNEL_ROW_TONES.DONE) return { tone: 'success', label: 'Completed' };
    if (isCurrent) return { tone: 'pending', label: 'Current step' };
    if (tone === FUNNEL_ROW_TONES.GAP) return { tone: 'missing', label: 'No data' };
    return { tone: 'neutral', label: 'Not reached' };
}

/**
 * Absolute timestamp for a row, or `null` when it has none.
 *
 * The year is kept — unlike the mock this is modelled on — because a journey
 * can span months and agents cross-check these against ledger dates, where a
 * missing year turns a stale record into a fresh-looking one.
 *
 * @param {number | null | undefined} atMs
 * @returns {string | null}
 */
export function formatFunnelTimestamp(atMs) {
    if (!Number.isFinite(atMs)) return null;
    const moment = dayjs(atMs);
    return moment.isValid() ? moment.format('MMM D, YYYY h:mm A') : null;
}

/**
 * Elapsed time as `1d 4h 18m` / `2h 50m` / `3m` / `45s`. Sub-minute gaps keep
 * seconds so consecutive automated steps don't all collapse to "0m".
 *
 * @param {number | null | undefined} ms
 * @returns {string | null}
 */
export function formatFunnelDuration(ms) {
    if (!Number.isFinite(ms) || ms < 0) return null;

    if (ms < MINUTE_MS) return `${Math.floor(ms / 1000)}s`;

    const totalMinutes = Math.floor(ms / MINUTE_MS);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days) {
        return [`${days}d`, hours ? `${hours}h` : null, minutes ? `${minutes}m` : null].filter(Boolean).join(' ');
    }
    if (hours) return minutes ? `${hours}h ${minutes}m` : `${hours}h`;
    return `${minutes}m`;
}

function humanizeDetailKey(key) {
    return String(key)
        .replace(/_/g, ' ')
        .replace(/^./, (char) => char.toUpperCase());
}

/**
 * Flattens a row's `detail` object into renderable chips, dropping every key
 * the backend sent as null — `transfer_type` and `fee` are null today for
 * advances, and an "Fee: —" chip would read as "no fee was charged" rather
 * than "we don't have that field yet".
 *
 * Nested objects are skipped rather than stringified into `[object Object]`.
 *
 * @param {object | null | undefined} detail
 * @returns {Array<{ key: string, label: string, value: string }>}
 */
export function formatFunnelDetail(detail) {
    if (!detail || typeof detail !== 'object') return [];

    return Object.entries(detail)
        .filter(
            ([key, value]) =>
                !HIDDEN_DETAIL_KEYS.has(key) &&
                value !== null &&
                value !== undefined &&
                value !== '' &&
                typeof value !== 'object',
        )
        .map(([key, value]) => {
            let display;
            if (typeof value === 'boolean') display = value ? 'Yes' : 'No';
            else if (DETAIL_CURRENCY_KEYS.has(key) && Number.isFinite(Number(value)))
                display = formatCurrency(Number(value));
            else display = String(value);

            return { key, label: DETAIL_LABELS[key] || humanizeDetailKey(key), value: display };
        });
}

/**
 * Enriches the raw milestone list with the two derived labels the timeline
 * renders: the gap since the previous event, and the total span of the
 * journey.
 *
 * The list arrives in FUNNEL-STAGE order. The backend now chains each stage to
 * the one before it, so for a normal member stage order and chronological order
 * agree and every gap is a real elapsed time.
 *
 * They can still disagree on the rows the backend fills from a *fallback*
 * signal — a legacy member with no payroll-link screens is credited from the
 * provider window, which is resolved later in the chain than the rows it backs.
 * Reordering by time is not an option there — stages with no timestamp would
 * scatter and the funnel would stop reading as a funnel — so each gap is
 * measured against the LATEST timestamp seen so far and omitted entirely when
 * it would be negative. A missing gap label is honest; "-4m" is not. A gap of
 * exactly zero IS shown, as `0s`: simultaneous steps are a real answer, an
 * absent label is not.
 *
 * @param {Array<object> | null | undefined} milestones
 * @returns {Array<object>} rows with `atMs`, `sinceMs` and `totalMs` added
 */
export function decorateFunnelMilestones(milestones) {
    const rows = Array.isArray(milestones) ? milestones : [];

    const timestamps = rows.map((row) => (Number.isFinite(row?.at_ms) ? row.at_ms : null));
    const known = timestamps.filter((value) => value !== null);
    const firstMs = known.length ? Math.min(...known) : null;
    const lastMs = known.length ? Math.max(...known) : null;
    // The row that closes the journey is the one holding the newest timestamp,
    // which — see above — is not necessarily the last row in the list.
    const spanIndex = lastMs === null ? -1 : timestamps.lastIndexOf(lastMs);

    let latestSoFar = null;

    return rows.map((row, index) => {
        const atMs = timestamps[index];

        let sinceMs = null;
        if (atMs !== null) {
            // `>=`, so two steps sharing a timestamp render an explicit `0s`
            // rather than no label at all. Several stages legitimately land on
            // the same instant — `account_created` and `onboarding_opened` are
            // both evidenced by the same `Welcome_Viewed` event — and a blank
            // there reads as missing data instead of "these were simultaneous".
            if (latestSoFar !== null && atMs >= latestSoFar) sinceMs = atMs - latestSoFar;
            latestSoFar = latestSoFar === null ? atMs : Math.max(latestSoFar, atMs);
        }

        const totalMs = index === spanIndex && firstMs !== null && lastMs > firstMs ? lastMs - firstMs : null;

        return { ...row, atMs, sinceMs, totalMs };
    });
}

/**
 * Whether the backend's Athena scan found this member in the lake at all.
 *
 * `has_events` is the endpoint's explicit answer, and this reads it STRICTLY:
 * only a literal `false` counts. An older service that predates the field
 * sends `undefined`, and treating that as "no events" would flip every funnel
 * on the page into an empty state the moment the admin is deployed ahead of
 * the API.
 *
 * Why a field rather than deriving it: the funnel ALWAYS returns nine rows, so
 * "no events" cannot be read off the list's length. Deriving it from the
 * timestamps means re-implementing a rule the backend already owns — including
 * its one non-obvious exclusion (`first_advance_taken` is Postgres-sourced and
 * resolves whether or not the lake answered, so it must not count) — and two
 * implementations of that rule would eventually disagree.
 *
 * @param {object | null | undefined} funnel
 * @returns {boolean}
 */
export function funnelFoundNoEvents(funnel) {
    return funnel?.has_events === false;
}

/**
 * The card's empty state, or `null` when there is a journey to render.
 *
 * Two different answers, because `has_events: false` has two very different
 * causes and an agent needs to tell them apart:
 *
 *  - **Nothing at all.** No events and no advance. The member genuinely has
 *    not started, which is the honest and common case.
 *  - **An advance with no events.** The member has taken money out of the
 *    product, so they cannot have skipped onboarding — the scan is looking in
 *    the wrong place (`ANALYTICS_ENVIRONMENT`, `fi_id`, or the window). The
 *    backend logs a warning naming all three. Saying "no events recorded"
 *    alone here would report a data gap as a fact about the member.
 *
 * @param {object | null | undefined} funnel
 * @returns {{ message: string, diagnostic: boolean } | null}
 */
export function getFunnelEmptyState(funnel) {
    if (!funnelFoundNoEvents(funnel)) return null;

    const hasOtherData = (funnel.milestones || []).some((row) => Number.isFinite(row?.at_ms));

    if (hasOtherData) {
        return {
            message:
                'No onboarding events were found for this member, but their ledger shows an advance — ' +
                'so they cannot have skipped onboarding. This is usually the analytics scan looking in the ' +
                'wrong place rather than a gap in the member’s history.',
            diagnostic: true,
        };
    }

    return {
        message: 'No journey events recorded for this member yet.',
        diagnostic: false,
    };
}

/**
 * The one thing the page is meant to answer: where is this member right now.
 *
 * Returned separately from the pills because it is rendered as the page's
 * visual anchor rather than as a chip — the whole point of the funnel is to
 * replace guessing at a member's state, so the answer has to be the first and
 * largest thing on the page, not a label inside a card further down.
 *
 * Returns `null` while there is nothing to state (no payload yet), so the
 * anchor can be skipped rather than rendering an empty heading.
 *
 * @param {object | null | undefined} funnel
 * @returns {{ title: string, description: string | null, complete: boolean,
 *             unknown: boolean, hasError: boolean, errorResolved: boolean } | null}
 */
export function getFunnelAnchor(funnel) {
    if (!funnel) return null;

    const hasError = Boolean(funnel.has_error);
    const errorResolved = Boolean(funnel.error_resolved);
    const currentStage = funnel.current_stage;

    // A scan that found nothing still returns `current_stage: 'account_created'`
    // — it is just the first stage nobody completed. Announcing that as where
    // the member is standing states a position we have no evidence for, in the
    // largest text on the page. Say what is actually true instead.
    if (funnelFoundNoEvents(funnel)) {
        return {
            title: 'No journey recorded',
            description: getFunnelEmptyState(funnel).message,
            complete: false,
            unknown: true,
            hasError,
            errorResolved,
        };
    }

    if (!currentStage) {
        return {
            title: 'Journey complete',
            description: 'This member finished onboarding and has taken their first advance.',
            complete: true,
            unknown: false,
            hasError,
            errorResolved,
        };
    }

    const stage = (funnel.milestones || []).find((row) => row.key === currentStage);

    return {
        // The key is a last resort: it is the API contract, not prose, but a
        // stage whose title never arrived is still better named than blank.
        title: stage?.title || currentStage,
        description: stage?.description || null,
        complete: false,
        unknown: false,
        hasError,
        errorResolved,
    };
}

/**
 * Header pills summarising the whole journey: where the member stands, and
 * whether they hit a payroll error that is still unresolved.
 *
 * @param {object | null | undefined} funnel
 * @returns {Array<{ key: string, tone: string, label: string }>}
 */
export function getFunnelSummaryPills(funnel) {
    if (!funnel) return [];

    const pills = [];
    const currentStage = funnel.current_stage;

    // Same reason as the anchor: with no events, `current_stage` is an artefact
    // of every stage being unreached, not a step the member is on.
    if (funnelFoundNoEvents(funnel)) {
        pills.push({ key: 'stage', tone: 'missing', label: 'No journey recorded' });
    } else if (!currentStage) {
        pills.push({ key: 'stage', tone: 'success', label: 'Journey complete' });
    } else {
        const stage = (funnel.milestones || []).find((row) => row.key === currentStage);
        pills.push({
            key: 'stage',
            tone: 'pending',
            label: `Current step: ${stage?.title || currentStage}`,
        });
    }

    if (funnel.has_error) {
        pills.push(
            funnel.error_resolved
                ? { key: 'error', tone: 'success', label: 'Error resolved' }
                : { key: 'error', tone: 'danger', label: 'Unresolved error' },
        );
    }

    return pills;
}

/**
 * Failure copy for the two funnel reads, which fail in the same ways and
 * differ only in what could not be loaded — hence `subject`, so the attempts
 * modal does not announce the journey's failure.
 *
 * A 400 is a statement about the id in the URL and a 403 one about the agent;
 * neither is something a retry can fix, so those say what happened without
 * offering a button that would just fail again.
 *
 * "This member has no journey" is deliberately NOT an error here: the endpoint
 * answers an id it knows nothing about with an empty funnel and a 200, so that
 * case arrives as data and the card renders it through `getFunnelEmptyState`,
 * keyed on the payload's own `has_events`. (It used to be keyed on the row
 * count, which never fired: the funnel always returns nine rows.) A 404
 * therefore never means "no data" — it means the request did not reach the
 * endpoint (the route is not deployed yet, or a proxy answered in the API's
 * place), so it falls through to the generic retryable copy rather than making
 * a claim about the member.
 *
 * @param {number | undefined} status
 * @param {string} [subject] What failed to load, in prose.
 * @returns {{ message: string, retryable: boolean }}
 */
export function resolveFunnelError(status, subject = 'onboarding journey') {
    if (status === 403) {
        return { message: `You do not have access to this member’s ${subject}.`, retryable: false };
    }
    if (status === 400) {
        return { message: `That member id is not valid, so their ${subject} could not be read.`, retryable: false };
    }
    return { message: `Could not load the ${subject}. Please try again.`, retryable: true };
}
